import React, { useEffect, useState } from "react";
import { cloneDeep, keyBy, sortBy } from "lodash";
import { Formik, Form, FieldArray } from "formik";
import { useQuery, useMutation } from "urql";
import { PartialDeep } from "type-fest";
import { useParams, useLocation, Link } from "wouter";
import { Badge } from "../../../components/src/Badge";
import { Button } from "../../../components/src/Button";

import { Alert, AlertVariant } from "../../../components/src/Alert";

import { Avatar } from "../../../components/src/avatar";
import { recursivelyRemoveKey } from "../../../common/src/utils";
import { usePrevious } from "../../../components/src/common/Utils";
import { SubmitButton, TextField, SelectField, MultipleSelectField } from "../../../components/src/form";
import { ReactMarkdownCustom } from "../../../components/src/form/ReactMarkdownCustom";
import { formikUrqlErrorFormater } from "../../../components/src/common/formik-urql-error-helper";

import { LinkLookup } from "./note/NoteComponents";
import { MarkDownField } from "../components/form/MarkDownField";
import { GET_TICKET_DATA, UPSERT_TICKETS } from "../../../graphql/src/ticket/queries";
import {
    TicketFilterInput,
    Ticket,
    TicketInput,
    TicketTypeEnum,
    TicketStatusEnum,
    TicketState,
    TicketReference,
    CollectionNameEnum,
    PermissionAssetEnum,
    Me,
    Party
} from "../types.generated";
import { SYSTEM_PARTY_ID } from "../Constants";
import { REACT_APP_API_URI } from "../env";

const defaultFormData: PartialDeep<Ticket> = {
    __typename: "Ticket",
    _id: null,
    title: "",
    clientId: SYSTEM_PARTY_ID,
    type: TicketTypeEnum.Error,
    creatorId: "",
    createTimestamp: "",
    updateTimestamp: "",
    references: [],
    state: {
        status: TicketStatusEnum.Open,
        assigneeIds: [],
        comment: "",
        updateUserId: "",
        timestamp: ""
    },
    previousStates: []
};

export const defaultReference: Partial<TicketReference> = {
    collection: CollectionNameEnum.None,
    documentId: ""
};

export const Reference = ({ reference }: { reference: Partial<TicketReference> }): React.ReactElement => {
    return <LinkLookup collection={reference.collection} documentId={reference.documentId} />;
};

const toLocaleString = (date: Date) => {
    return date.toLocaleString("en-EN", {
        weekday: "short", // long, short, narrow
        day: "numeric", // numeric, 2-digit
        year: "numeric", // numeric, 2-digit
        month: "long", // numeric, 2-digit, long, short, narrow
        hour: "numeric", // numeric, 2-digit
        minute: "numeric", // numeric, 2-digit
        second: "numeric" // numeric, 2-digit
    });
};

export const TicketPage = (): React.ReactElement => {
    const [location, navigate] = useLocation();
    const { id }: any = useParams();
    const [editReferences, setEditReferences] = useState<boolean>(false);
    const previousId: string = usePrevious(id);
    const [formData, setFormData] = useState(null);
    const filter: TicketFilterInput = {
        idIn: [id === "new" ? "000000000000000000000000" : id],
        statusIn: [TicketStatusEnum.Open, TicketStatusEnum.Closed]
    };
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });
    const [{ fetching: loading, error, data }, refetch] = useQuery<{
        me?: Partial<Me>;
        clients?: Partial<Party>[];
        ticketUsers?: Array<{ _id: string; name: string; clientIds?: string[] }>;
        tickets?: Partial<Ticket>[];
    }>({ query: GET_TICKET_DATA, variables: { filter: filter } });

    const [_, upsertTickets] = useMutation(UPSERT_TICKETS);

    useEffect(() => {
        if (previousId && previousId !== id) {
            setFormData(null);
            refetch();
        } else if (data && formData === null && id === "new") {
            const initFormData = cloneDeep(defaultFormData);
            setFormData(initFormData);
        } else if (formData === null && data && data.tickets.length === 1) {
            const init = cloneDeep(data.tickets[0]);
            init.state.comment = "";
            setFormData(init);
        }
    }, [previousId, id, data, formData, refetch]);

    if (loading) return <p>Loading...</p>;
    if (error) {
        return (
            <div>
                <h3>error</h3>
                <p>{error.toString()}</p>;
            </div>
        );
    }

    if (id !== "new" && data.tickets.length === 0) {
        return <div>No ticket with id {id}</div>;
    }

    if (!formData) {
        return <div></div>;
    }

    const usersByClientId: Record<string, { key: string; value: string }[]> = {};

    data.ticketUsers.forEach((user) => {
        user.clientIds.forEach((clientId: string) => {
            if (clientId in usersByClientId) {
                usersByClientId[clientId].push({ key: user._id, value: user.name });
            } else {
                usersByClientId[clientId] = [{ key: user._id, value: user.name }];
            }
        });
    });

    // TODO: need to add a query to get the available users
    let ticketUsers = [{ key: data.me._id, value: data.me.name }];

    if (formData.clientId in usersByClientId) {
        ticketUsers = usersByClientId[formData.clientId];
    }

    //console.log("formData: ", formData);

    const openTicket = formData.state.status === TicketStatusEnum.Open;

    return (
        <div className="mx-auto px-4">
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-2/12 px-2 mb-4">
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        className="mb-3"
                        onClick={() => {
                            const win = window.open("/tickets", "_self");
                            win.focus();
                        }}
                    >
                        All tickets
                    </Button>
                </div>
                <div className="w-full md:w-10/12 px-2 mb-4 text-right">
                    {data &&
                    data.me &&
                    data.me.frontendRole &&
                    data.me.frontendRole.assets &&
                    (data.me.frontendRole.assets.includes(PermissionAssetEnum.BackOffice) || data.me.frontendRole.assets.length === 0) ? (
                        <a
                            href="https://github.com/CaptorAB/documents/blob/master/BackOffice/manual"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Manuals
                        </a>
                    ) : null}
                </div>
            </div>
            <Formik
                enableReinitialize={true}
                initialValues={formData}
                validate={() => {
                    const errors: any = {};

                    return Object.keys(errors).length > 0 ? errors : {};
                }}
                onSubmit={async (submitValues, { setSubmitting, setErrors }) => {
                    let input: TicketInput[] = [
                        {
                            _id: submitValues._id === "new" ? null : submitValues._id,
                            title: submitValues.title,
                            clientId: submitValues.clientId,
                            type: submitValues.type,
                            references: submitValues.references,
                            state: {
                                status: submitValues.setStatus ? submitValues.setStatus : submitValues.state.status,
                                assigneeIds: submitValues.state.assigneeIds,
                                comment: submitValues.state.comment
                            }
                        }
                    ];

                    //Mentioning what has been changed in the comment
                    if (input[0]._id) {
                        const clientsById = keyBy(data.clients, "_id");
                        if (input[0].clientId !== formData.clientId) {
                            input[0].state.comment =
                                input[0].state.comment + " Client updated to " + clientsById[input[0].clientId].name + ".";
                        }

                        if (input[0].type !== formData.type) {
                            input[0].state.comment = input[0].state.comment + " Type updated to " + input[0].type + ".";
                        }

                        if (JSON.stringify(input[0].state.assigneeIds.sort()) !== JSON.stringify(formData.state.assigneeIds.sort())) {
                            const userNamesByIds: Record<string, string> = {};

                            for (const user of usersByClientId[input[0].clientId]) {
                                userNamesByIds[user.key] = user.value;
                            }
                            const assigneeNames: string[] = [];
                            for (const assigneeId of input[0].state.assigneeIds) {
                                assigneeNames.push(userNamesByIds[assigneeId]);
                            }
                            input[0].state.comment = input[0].state.comment + " AssigneeIds updated to " + assigneeNames.toString() + ".";
                        }
                        if (
                            JSON.stringify(sortBy(formData.references, "documentId")) !==
                            JSON.stringify(sortBy(input[0].references, "documentId"))
                        ) {
                            const referenceCollectionNames: CollectionNameEnum[] = [];

                            for (const reference of input[0].references) {
                                if (reference.collection) {
                                    referenceCollectionNames.push(reference.collection);
                                }
                            }
                            input[0].state.comment =
                                input[0].state.comment + " References updated to " + referenceCollectionNames.toString() + ".";
                        }
                    }

                    input = recursivelyRemoveKey(input, "__typename");

                    await upsertTickets({ input })
                        .then((result) => {
                            if ("error" in result && result.error) {
                                const message = formikUrqlErrorFormater(result.error, setErrors);
                                setAlert({ color: "danger", visible: true, message });
                            } else {
                                if (id === "new") {
                                    const pathname = location.split("?")[0].split("#")[0];
                                    const path = pathname.split("/");
                                    path.pop();
                                    path.push(result.data.upsertTickets[0]._id);
                                    navigate(path.join("/"), { replace: true });
                                } else {
                                    //setAlert({ color: "success", visible: true, message: `The ticket has been updated successfully!` });
                                    const init = cloneDeep(result.data.upsertTickets[0]);
                                    init.state.comment = "";
                                    setFormData(init);
                                    setSubmitting(false);
                                }
                            }
                        })
                        .catch((error) => {
                            setAlert({ color: "danger", visible: true, message: error.toString() });
                            setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting, values: temp, setFieldValue, handleSubmit }) => {
                    const values: PartialDeep<Ticket> = temp;

                    return (
                        <Form autoComplete="off">
                            <div className="flex flex-wrap -mx-2">
                                {id === "new" ? (
                                    <div>
                                        <div className="w-full md:w-10/12 px-2 mb-4">
                                            <TextField
                                                name="title"
                                                label={<h3>Title</h3>}
                                                type="text"
                                                className=""
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="w-full md:w-10/12 px-2 mb-4">
                                            <h1>{values.title}</h1>
                                            <div className="flex items-baseline">
                                                <h5>
                                                    <Badge variant="solid" color={openTicket ? "success" : "danger"} size="2" radius="full">
                                                        <div className="p-2">{values.state.status}</div>
                                                    </Badge>
                                                </h5>
                                                <div className="ml-2">
                                                    <strong>{values.creator.name}</strong>{" "}
                                                    {`opened this issue on ${toLocaleString(new Date(values.createTimestamp))}`}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-wrap -mx-2">
                                <div className="w-full md:w-8/12 px-2 mb-4">
                                    {values.previousStates.map((state: Partial<TicketState>, index) => {
                                        const date = new Date(state.timestamp);
                                        return (
                                            <div key={`n${index}`} className="flex flex-wrap -mx-2">
                                                <div className="w-full md:w-1/12 pt-4 px-2 mb-4">
                                                    <div className="avatar-timeline"></div>

                                                    <Avatar name={state.updateUserInfo.name} round={true} size={40} />
                                                </div>
                                                <div className="w-full md:w-11/12 mt-4 px-2 mb-4">
                                                    <div key={index} className="bg-white border border-gray-300 rounded-lg shadow-sm">
                                                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-300 rounded-t-lg">
                                                            <strong>{state.updateUserInfo.name}</strong>
                                                            {" commented on " + toLocaleString(date)}
                                                        </div>
                                                        <div className="px-4 py-3">
                                                            <ReactMarkdownCustom>{state.comment || ""}</ReactMarkdownCustom>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    <ul className="nav nav-tabs mt-4" id="comment-tab" role="tablist">
                                        <li className="list-none">
                                            <a
                                                className="block px-4 py-2 text-sky-600 border-b-2 border-sky-600 cursor-pointer"
                                                id="comment-tab-a"
                                                data-toggle="tab"
                                                role="tab"
                                                aria-selected="true"
                                            >
                                                Write a comment
                                            </a>
                                        </li>
                                    </ul>

                                    <MarkDownField
                                        name="state.comment"
                                        graphqlApiBaseUrl={REACT_APP_API_URI}
                                        label="."
                                        type="text"
                                        className=""
                                        height={300}
                                        initialEditMode={true}
                                        disabled={isSubmitting}
                                        useAttachment={true}
                                    />

                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full md:w-12/12 mt-4 px-2">
                                            {alert.visible ? (
                                                <Alert
                                                    style={{ marginTop: "10px" }}
                                                    variant={alert.color}
                                                    onClose={onDismissAlert}
                                                    dismissible
                                                >
                                                    {alert.message}
                                                </Alert>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="flex flex-row-reverse">
                                        <SubmitButton
                                            className="btn btn-success ml-2"
                                            disabled={false}
                                            label={id === "new" ? "Open" : "Update"}
                                        />
                                        {id !== "new" ? (
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className={openTicket ? "btn btn-outline-danger" : "btn btn-outline-success"}
                                                onClick={() => {
                                                    setFieldValue(
                                                        "setStatus",
                                                        openTicket ? TicketStatusEnum.Closed : TicketStatusEnum.Open,
                                                        false
                                                    );
                                                    handleSubmit();
                                                }}
                                            >
                                                {openTicket ? "Close issue" : "Re-open issue"}
                                            </button>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="w-full md:w-4/12 mt-4 px-2">
                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full md:w-12/12 mt-4 px-2">
                                            <SelectField
                                                name="clientId"
                                                label={<Link to={"/parties/" + values.clientId}>Client</Link>}
                                                options={data.clients.map((d) => ({ key: d._id, value: d.name }))}
                                                className=""
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full md:w-12/12 mt-4 px-2">
                                            <SelectField
                                                className=""
                                                name="type"
                                                label="Type"
                                                options={Object.values(TicketTypeEnum)}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap -mx-2 mt-4">
                                        <h6
                                            style={{ color: "#0284c7" }}
                                            className="w-full md:w-12/12 mt-4 px-2 font-weight-bold"
                                            onClick={() => {
                                                setEditReferences(!editReferences);
                                            }}
                                        >
                                            References
                                        </h6>
                                    </div>
                                    {editReferences ? (
                                        <div className="w-full md:w-12/12 mt-4 px-2">
                                            <FieldArray
                                                name="references"
                                                render={(arrayHelpers) => (
                                                    <div className="flex flex-wrap -mx-2">
                                                        {values.references && values.references.length > 0 ? (
                                                            values.references.map((tag, index) => (
                                                                <div key={index} className="w-full md:w-6/12 mt-4 px-2">
                                                                    <SelectField
                                                                        className=""
                                                                        name={`references[${index}].collection`}
                                                                        label="Collection"
                                                                        options={Object.values(CollectionNameEnum).sort()}
                                                                        disabled={isSubmitting}
                                                                    />
                                                                    <TextField
                                                                        name={`references[${index}].documentId`}
                                                                        label={
                                                                            <LinkLookup
                                                                                collection={tag.collection}
                                                                                documentId={tag.documentId}
                                                                            />
                                                                        }
                                                                        className=""
                                                                        disabled={false}
                                                                    />
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        className="mr-1"
                                                                        type="button"
                                                                        onClick={() => arrayHelpers.remove(index)}
                                                                    >
                                                                        -
                                                                    </Button>
                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        type="button"
                                                                        onClick={() =>
                                                                            arrayHelpers.insert(index, cloneDeep(defaultReference))
                                                                        }
                                                                    >
                                                                        +
                                                                    </Button>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                type="button"
                                                                onClick={() => arrayHelpers.push(cloneDeep(defaultReference))}
                                                            >
                                                                {/* show this when user has removed all items */}
                                                                Add a reference
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            {values.references.map((reference, index) => {
                                                return <Reference key={"reference" + index.toString()} reference={reference} />;
                                            })}
                                        </div>
                                    )}

                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full md:w-12/12 mt-4 px-2">
                                            <MultipleSelectField
                                                name="state.assigneeIds"
                                                label="Assignees"
                                                options={ticketUsers}
                                                className=""
                                                disabled={isSubmitting}
                                                size={ticketUsers.length}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};
